import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { DownloadIcon } from "@chakra-ui/icons";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Center,
  Button,
  Select,
  Text,
  useToast,
  Input,
} from "@chakra-ui/react";
import { round } from "lodash";
import moment from "moment";
import * as XLSX from "xlsx";

import DateRangePicker from "../../../components/DateRangePicker";
import OrderService from "../../../services/createOrderService";

const payoutStatusOptions = [
  { label: "Payout Initiated", value: "initiated" },
  { label: "Payment In Transit", value: "fund_processing" },
  { label: "Payout Successful", value: "fund_settled" },
  { label: "Failed", value: "fund_failed" },
  { label: "Fund Scheduled", value: "fund_scheduled" },
  { label: "Expired", value: "expired" },
  { label: "Manual Review", value: "manual_review" },
];

const payinStatusOptions = [
  { label: "Initiated", value: "initiated" },
  { label: "Fund Processing", value: "fund_processing" },
  { label: "Payment Received", value: "fund_settled" },
  { label: "Order Failed", value: "fund_failed" },
  { label: "Expired", value: "expired" },
  { label: "Manual Review", value: "manual_review" },
];

const gamingStatusOptions = [
  { label: "Initiated", value: "initiated" },
  { label: "Asset Deposited", value: "asset_deposited" },
  { label: "Fund Processing", value: "fund_processing" },
  { label: "Fund Settled", value: "fund_settled" },
  { label: "Fund Failed", value: "fund_failed" },
  { label: "Completed", value: "completed" },
  { label: "Expired", value: "expired" },
  { label: "Manual Review", value: "manual_review" },
];

const prefundStatusOptions = [
  { label: "Prefund Initiated", value: "initiated" },
  { label: "Prefund Successful", value: "completed" },
  { label: "Prefund Failed", value: "asset_deposit_failed" },
  { label: "Payment Received", value: "fund_settled" },
  { label: "Prefund Processing", value: "fund_processing" },
];

const payoutHeaders = [
  "Order ID TransFi",
  "Order Status",
  "Sender Email",
  "Recipient Name",
  "Recipient Email",
  "Purpose of Transaction",
  "Initiated Date",
  "Receiver Approved Date",
  "Payment Complete Date",
  "Prefund Currency",
  "Amount Deducted From  Prefund",
  "Recipient's Payment Method",
  "Recipient's Fiat Currency",
  "Gross TXN Amount on Fiat",
  "Processing Fees %",
  "Processing Fees Amount",
  "Fiat Delivered To Receiver",
  "Sender's Crypto Wallet Address",
];

const payinHeaders = [
  "Order ID TransFi",
  "Order Status",
  "Settlement Status",
  "Sender Name",
  "Sender Email",
  "Purpose of Transaction",
  "Initiated Date",
  "Date and Time invoice paid",
  "Sender's Payment Method",
  "Sender's Currency",
  "Gross Fiat Amount",
  "Processing Fees Amount",
  "Net Amount",
  "Conversion rate charged by TransFi",
  "Crypto To Be Sold",
  "Receiver's Crypto Wallet Address",
  "Transaction hash",
];

const gamingHeaders = [
  "Order ID TransFi",
  "Customer Order ID",
  "Order Status",
  "Sender Name",
  "Purpose of Transaction",
  "Initiated Date",
  "Settled Date",
  "Fiat Payment Method",
  "Fiat Currency",
  "Gross Fiat Amount (Received from the user)",
  "Processing Fees Amt.",
  "Net Amount",
  "Conversion rate charged by TransFi",
  "Crypto To Be Sold",
  "Digital Asset Sent",
  "Crypto Ticker",
  "Crypto Wallet Address",
  "Transaction hash",
];

const prefundHeaders = [
  "Prefund ID",
  "Order Status",
  "Created Date",
  "Processing Date",
  "Completed Date",
  "Deposit Amount",
  "Deposit Currency",
  "Prefund Wallet Address",
  "Senders Wallet Address",
];

const getRows = (row, type) => {
  const payoutRows = {
    "Order ID TransFi": row?.orderId || "",
    "Order Status": row?.status || "",
    "Sender Email": row?.customer?.email || "",
    "Recipient Name": row?.recipientDetails?.firstName || "" + " " + row?.recipientDetails?.lastName || "",
    "Recipient Email": row?.recipientDetails?.email || "",
    "Purpose of Transaction": row?.purposeCode || "",
    "Initiated Date": row?.createdAt ? moment(row.createdAt).format("DD-MMM-YYYY @ h:mm A") : "",
    "Receiver Approved Date": row?.timestamps?.fundScheduledAt ? moment(row.timestamps.fundScheduledAt).format("DD-MMM-YYYY @ h:mm A") : "",
    "Payment Complete Date": row?.timestamps?.fundSettledAt ? moment(row.timestamps.fundSettledAt).format("DD-MMM-YYYY @ h:mm A") : "",
    "Prefund Currency": row?.depositCurrency || "",
    "Amount Deducted From Prefund": row?.depositAmount || 0,
    "Recipient's Payment Method": row?.paymentName || "",
    "Recipient's Fiat Currency": row?.fiatTicker || "",
    "Gross TXN Amount on Fiat": row?.grossTxnAmountOnFiat || 0,
    "Processing Fees %": row?.processingFees || 0,
    "Processing Fees Amount": row?.totalFee || 0,
    "Fiat Delivered To Receiver": row?.fiatAmount || 0,
    "Sender's Crypto Wallet Address": row?.walletAddress || "",
  };

  const payinRows = {
    "Order ID TransFi": row?.orderId || "",
    "Order Status": row?.status || "",
    "Settlement Status": row?.settlementStatus || "",
    "Sender Name": row?.senderName?.firstName || "",
    "Sender Email": row?.email || "",
    "Purpose of Transaction": row?.purposeCode || "",
    "Initiated Date": row?.timestamps?.initiatedAt ? moment(row?.timestamps?.initiatedAt).format("DD-MMM-YYYY @ h:mm A") : "-",
    "Date and Time invoice paid": row?.timestamps?.fundSettledAt ? moment(row?.timestamps?.fundSettledAt).format("DD-MMM-YYYY @ h:mm A") : "-",
    "Sender's Payment Method": row?.paymentFormattedName || "",
    "Sender's Currency": row?.fiatTicker || "",
    "Gross Fiat Amount": row?.fiatAmount || "",
    "Processing Fees Amount": row?.fees?.processingFee || "",
    "Net Amount": row?.fiat?.netAmount || "",
    "Conversion rate charged by TransFi": row?.crypto?.conversionRate || "",
    "Crypto To Be Sold": row?.withdrawAmount,
    "Receiver's Crypto Wallet Address": row?.walletAddress,
    "Transaction hash": row?.crypto?.transactionHash || "",
  };

  const gamingRows = {
    "Order ID TransFi": row?.orderId || "",
    "Customer Order ID": row?.customer?.orderId || "",
    "Order Status": row?.status || "",
    "Sender Name": `${row?.senderName?.firstName || ""} ${row?.senderName?.lastName || ""}`,
    "Purpose of Transaction": row?.purposeCode || "",
    "Initiated Date": row?.timestamps?.initiatedAt ? moment(row?.timestamps?.initiatedAt).format("DD-MMM-YYYY @ h:mm A") : "-",
    "Settled Date": row?.timestamps?.completedAt ? moment(row?.timestamps?.completedAt).format("DD-MMM-YYYY @ h:mm A") : "-",
    "Fiat Payment Method": row?.paymentName || "",
    "Fiat Currency": row?.fiat?.currency || "",
    "Gross Fiat Amount (Received from the user)": row?.fiat?.orderAmount || "",
    "Processing Fees Amt.":
      row?.fees?.feeMode === "hybrid" ? (row?.fees?.processingFee || 0) + (row?.fees?.fixedFee?.totalFixedFees || 0) : row?.fees?.processingFee || 0,
    "Net Amount": row?.fees?.feeMode === "hybrid" ? row?.fiat?.netAmount || 0 : row?.fiat?.netAmount || 0,
    "Conversion rate charged by TransFi": row?.crypto?.conversionRate ? round(row?.crypto?.conversionRate, 8) : "0",
    "Crypto To Be Sold": row?.withdrawAmount || 0,
    "Digital Asset Sent": row?.crypto?.orderAmount || "N/A",
    "Crypto Ticker": row?.withdrawCurrency || "",
    "Crypto Wallet Address": row?.crypto?.withdrawalId || "N/A",
    "Transaction hash": row?.crypto?.withdrawTxnHash || "N/A",
  };

  const prefundRows = {
    "Prefund ID": row?.pfId,
    "Order Status": row?.status,
    "Created Date": row?.createdAt ? moment(row?.createdAt).format("DD-MMM-YYYY @ h:mm A") : "N/A",
    "Processing Date": row?.timestamps?.assetProcessingAt ? moment(row?.timestamps?.assetProcessingAt).format("DD-MMM-YYYY @ h:mm A") : "N/A",
    "Completed Date": row?.timestamps?.completedAt ? moment(row?.timestamps?.completedAt).format("DD-MMM-YYYY @ h:mm A") : "N/A",
    "Deposit Amount": row?.depositAmount ?? "N/A",
    "Deposit Currency": row?.depositCurrency ?? "N/A",
    "Prefund Wallet Address": row?.walletAddress ?? "N/A",
    "Senders Wallet Address": row?.sendersWalletAddress ?? "N/A",
    "Transaction Hash": row?.crypto?.txnHash ?? "N/A",
  };

  const rowsMapping = {
    payin: payinRows,
    payout: payoutRows,
    gaming: gamingRows,
    prefund: prefundRows,
  };

  const rows = rowsMapping[type];
  return rows;
};

const statusOption = {
  payin: payinStatusOptions,
  payout: payoutStatusOptions,
  gaming: gamingStatusOptions,
  prefund: prefundStatusOptions,
};

const headers = {
  payin: payinHeaders,
  payout: payoutHeaders,
  gaming: gamingHeaders,
  prefund: prefundHeaders,
};

const DownloadReportDrawer = ({ onClose, isOpen, type }) => {
  const toast = useToast();

  const [dateRange, setDateRange] = useState({
    startDate: moment(new Date()).subtract(6, "days").toISOString(),
    endDate: moment(new Date()).toISOString(),
  });
  const [orders, setOrders] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");

  const orgId = useSelector((state) => state?.login?.orgDetails?.orgId);

  const onChangeDateRange = (start, end) => {
    setDateRange({ startDate: moment(start).toISOString(), endDate: moment(end).toISOString() });
  };

  const handleDownloadReport = async () => {
    setIsLoading(true);
    const data = await getOrderData({ type });
    console.log(data?.list);
    if (!data?.list || data.list.length === 0) {
      setIsLoading(false);
      toast({
        title: "Opps",
        description: "No orders with this filter (email and orderId) to download",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setOrders(data.list);
  };

  useEffect(() => {
    if (orders.length > 0) {
      downloadAsXLSX();
      setIsLoading(false);
    }
  }, [orders]);

  const getOrderData = async (filter) => {
    setIsLoading(true);
    const filters = {
      ...(selectedStatus ? { status: selectedStatus } : {}),
      ...(orderId ? { orderId: orderId } : {}),
      ...(email ? { email: email } : {}),
      ...(dateRange?.startDate ? { startDate: dateRange?.startDate } : {}),
      ...(dateRange?.endDate ? { endDate: dateRange?.endDate } : {}),
    };

    filters["orgId"] = orgId;
    filters["type"] = filter?.type === "prefund" ? ["crypto_prefund", "fiat_prefund"] : filter?.type;
    const { data = {} } = await OrderService.getOrder({ filters });
    setIsLoading(false);
    return data?.data;
  };

  const downloadAsXLSX = () => {
    setIsLoading(true);
    const timestamp = moment().format("DD-MM-YYYY");
    const filename = `Orders_report_${timestamp}.xlsx`;

    if (!orders || orders.length === 0) {
      setIsLoading(false);
      toast({
        title: "Opps",
        description: "No orders report to download",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const excelData = orders.map((row) => getRows(row, type));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    XLSX.utils.sheet_add_aoa(worksheet, [headers[type]], { origin: "A1" });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders Report");

    XLSX.writeFile(workbook, filename);

    setIsLoading(false);
  };

  return (
    <>
      <Drawer onClose={onClose} isOpen={isOpen} size={"sm"}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px">Order Filter</DrawerHeader>
          <DrawerBody>
            <Text>Select date range</Text>
            <div style={{ margin: "8px 0" }}>
              <DateRangePicker width={"100%"} onChangeDateRange={onChangeDateRange} start={dateRange.startDate} end={dateRange.endDate} />
            </div>
            <Text>Order Status</Text>
            <div style={{ margin: "8px 0" }}>
              <Select placeholder="All" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                {statusOption[type]?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <Text>Order ID</Text>
            <Input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Enter Order ID" mb={4} />

            <Text>Email</Text>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter Email" mb={4} />
            <Center mt={10}>
              <Button
                backgroundColor="#1A4FD6"
                _hover={{ bg: "#5a87fa" }}
                textColor="white"
                leftIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
                isLoading={isLoading}
              >
                Download Report
              </Button>
            </Center>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default DownloadReportDrawer;
